import {
  ReactElement,
  useMemo,
  useState,
  useEffect,
  useCallback,
  useContext,
} from 'react';
import { GetServerSideProps, NextApiRequest } from 'next';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptionsMode } from '@stripe/stripe-js';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { CART_ITEMS_URL } from '~/constants/cart';
import { USER_CREDITS_URL } from '~/constants/accounts';
import { DiscountDataType } from '~/types/account';
import { CheckoutProvider } from '~/providers/checkout';
import { UserObjectProps } from '~/types/user';
import { fetchJson } from '~/lib/fetch';
import { getCookieString } from '~/lib/util';
import { CartProps } from '~/types/cart';
import { PaymentMethodProps } from '~/types/checkout';
import { useCart } from '~/lib/cart';
import { enroll } from '~/lib/experiment';
import { USER_SESSION_ENDPOINT_URL } from '~/lib/login/constants';
import {
  CHECKOUT_HIDE_DISTRACTIONS_EXPERIMENT_NAME,
  CREDITS_COUPONS_EXPERIMENT_NAME,
} from '~/lib/util/constants';
import { PAYMENT_METHODS_URL } from '~/constants/checkout';
import { getCheckoutDataGA4, gtag } from '~/lib/util/dataLayer';
import { cartContainsPlusDeal } from '~/lib/checkout/util';

import CheckoutLayout from '~/layouts/CheckoutLayout';
import { getFontFamilyClass } from '~/layouts/util';
import { Container, ErrorNotification } from '~/components/checkout';
import { KlarnaMessage } from '~/components/global/KlarnaMessage';
import { GlobalContext } from '~/contexts/global';

interface QueryProps {
  initialCart?: CartProps;
  initialUser?: UserObjectProps;
  paymentError: string | string[];
  isCreditsCouponsEnabled: boolean;
  discountData: DiscountDataType | null;
  initialPaymentMethods?: Array<PaymentMethodProps>;
  hideDistractions: boolean;
}

const STRIPE_PUBLIC_KEY = process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY ?? '';
const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? '';
const STRIPE_OPTIONS_OBJECT = {
  mode: 'payment',
  currency: 'usd',
  amount: 50, // Minimum amount to make a purchase
  paymentMethodCreation: 'manual',
  // Fully customizable with appearance API.
  appearance: {
    /*...*/
  },
} as StripeElementsOptionsMode;

function Checkout({
  initialCart,
  initialUser,
  initialPaymentMethods,
  paymentError,
  isCreditsCouponsEnabled = false,
  hideDistractions = false,
}: Readonly<QueryProps>): ReactElement {
  const [stripeOptions, setStripeOptions] = useState<any>(
    STRIPE_OPTIONS_OBJECT,
  );
  const { cart, mutateItems } = useCart(initialCart);
  const [currentBillingCountry, setCurrentBillingCountry] =
    useState<string>('');

  // Stripe config, use useMemo to prevent re-rendering warnings
  const stripePromise = useMemo(
    () => loadStripe(STRIPE_PUBLIC_KEY),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const { hasUserDataOnDatalayer } = useContext(GlobalContext);

  // Update the stripe options object when the cart total changes
  useEffect(() => {
    const cartTotal = Math.round((cart?.total || 0) * 100);
    // Stripe options amount must be in cents
    if (cartTotal && cartTotal !== stripeOptions?.amount) {
      setStripeOptions({
        ...stripeOptions,
        amount: cartTotal,
      });
    }
  }, [cart?.total, stripeOptions]);

  // We don't need to update this since gtag is already defined
  const sendGA4CheckoutEvent = useCallback((cart: CartProps) => {
    const eventData = getCheckoutDataGA4(cart);

    gtag('event', 'begin_checkout', eventData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Send checkout event to GA4
  useEffect(() => {
    if (!hasUserDataOnDatalayer) return;

    /**
     * Timeout delays the call and will add the event after the ga4 hook on the layout.
     * This is because effects are called from children to parent and we need to set this
     * after the one on the layout.
     *
     * More info: https://github.com/appsumo/appsumo-next/pull/214#discussion_r1195415137
     **/
    setTimeout(() => {
      if (cart?.cart_items.length) {
        sendGA4CheckoutEvent(cart);
      }
    });
  }, [cart, hasUserDataOnDatalayer, sendGA4CheckoutEvent]);

  const cartHasPlusDeal = cartContainsPlusDeal(cart);

  // needed to reflect data from any cart fetchJson query params in get server side props
  // e.g. if "auto_apply_credits" is true, the user will see the credits applied in:
  //      payment section + summary
  useEffect(() => {
    if (cart?.use_credit !== initialCart?.use_credit) {
      mutateItems();
    }
  }, [cart?.use_credit, initialCart?.use_credit, mutateItems]);

  return (
    <Elements stripe={stripePromise} options={stripeOptions}>
      <GoogleReCaptchaProvider
        reCaptchaKey={RECAPTCHA_SITE_KEY}
        container={{
          parameters: {
            badge: 'bottomright',
            theme: 'dark',
          },
        }}
      >
        <CheckoutProvider
          initialCart={initialCart}
          initialUser={initialUser}
          initialPaymentMethods={initialPaymentMethods}
          currentBillingCountry={currentBillingCountry}
          setCurrentBillingCountry={setCurrentBillingCountry}
          hideDistractions={hideDistractions}
        >
          <ErrorNotification redirectPaymentError={paymentError === 'true'} />
          {!cartHasPlusDeal && (
            <div className="md:hidden">
              <KlarnaMessage currentBillingCountry={currentBillingCountry} />
            </div>
          )}
          <div className="mx-auto max-w-screen-2xl px-4 pt-2 md:pt-10">
            <Container
              cartHasPlusDeal={cartHasPlusDeal}
              isCreditsCouponsEnabled={isCreditsCouponsEnabled}
            />
          </div>
        </CheckoutProvider>
      </GoogleReCaptchaProvider>
    </Elements>
  );
}

Checkout.getLayout = function getLayout(
  page: React.ReactNode,
  { hideDistractions }: { hideDistractions: boolean },
): ReactElement {
  // Layout pacing was changed to allow for the klarna header to be above the checkout page
  return (
    <CheckoutLayout
      hideDistractions={hideDistractions}
      className={`${getFontFamilyClass()} flex min-h-screen flex-col font-sans`}
    >
      <div className="mt-[73px]">{page}</div>
    </CheckoutLayout>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context,
): Promise<{ props: QueryProps }> => {
  const { req, query } = context;
  const cookieString = getCookieString(req as NextApiRequest);
  const headers = {
    cookie: cookieString,
  };
  const { payment_error = '' } = query;
  let cart, user, paymentMethods; // Keep undefined in case of error

  const fetchOptions = {
    method: 'GET',
    include: 'credentials',
    headers,
  };

  try {
    // Added a query param to make sure the auto campaign coupon is applied
    // This may not be automatically added if the user goes directly to the cart page
    [cart, user] = await Promise.all([
      fetchJson(
        `${CART_ITEMS_URL}?apply_auto_campaign_coupon=true&auto_apply_credits=true`,
        fetchOptions,
      ),
      fetchJson(USER_SESSION_ENDPOINT_URL, fetchOptions),
    ]);
    // we're not checking possible_plan_discount here since sidebar component doesn't rely on non-zero value
  } catch (error: any) {
    // This shouldn't happen, should show in sentry
    console.error(error);
  }

  // Existing payment methods not 100% needed for checkout, query separately in case of error
  try {
    paymentMethods = await fetchJson(PAYMENT_METHODS_URL, fetchOptions);
  } catch (error: any) {
    // This shouldn't happen, should show in sentry
    console.error(error);
  }

  // enroll into experiment / fetch coupons
  let creditsCouponsVariant = 'disabled';
  let checkoutDistractionsVariant = 'disabled';
  let discountData: DiscountDataType | null = null;
  try {
    // fetching USER_CREDITS_URL for anon user errors out, so only fetch if user is authenticated
    [creditsCouponsVariant, checkoutDistractionsVariant, discountData] =
      await Promise.all([
        enroll(CREDITS_COUPONS_EXPERIMENT_NAME, context).catch(
          () => 'disabled',
        ),
        !user?.user?.id
          ? enroll(CHECKOUT_HIDE_DISTRACTIONS_EXPERIMENT_NAME, context).catch(
              () => 'disabled',
            )
          : Promise.resolve('disabled'),
        user?.user?.id
          ? fetchJson(USER_CREDITS_URL, fetchOptions).catch(() => null)
          : Promise.resolve(null),
      ]);
  } catch (error: any) {
    // This shouldn't happen, should show in sentry
    console.error(error);
  }

  return {
    props: {
      initialCart: cart,
      initialUser: user,
      initialPaymentMethods: paymentMethods,
      paymentError: payment_error,
      isCreditsCouponsEnabled: creditsCouponsVariant === 'enabled',
      hideDistractions: checkoutDistractionsVariant === 'enabled',
      discountData,
    },
  };
};

export default Checkout;
